/**
 * playerStore — o que o player flutuante do LeadTalk/LeadMusic está tocando e a fila (protótipo
 * "Vitrine + player flutuante com fila", 22/10/2026). Fica fora da página de conteúdos para o
 * player seguir tocando enquanto a pessoa navega: a vitrine só pede, e o PlayerHost (no
 * AppShell) mantém o player montado. Um item puxa o outro, na ordem da série ou embaralhado.
 */
import { create } from 'zustand';
import type { MultimediaFormat } from '../../api/useContents';

export type PlayerSource = MultimediaFormat;
export type PlayerRepeat = 'off' | 'all' | 'one';

export interface PlayerTrack {
  /** Id do vídeo no YouTube. */
  id: string;
  title: string;
  /** Série do episódio (LeadTalk) ou "LeadMusic". */
  sub: string;
  img?: string;
  /** Capa grande, para o destaque da vitrine (sem ela, vale `img`). */
  imgLarge?: string;
  /** Segundos; ausente quando o backend não mandou. */
  duration?: number | null;
}

/** De onde se está tocando: uma série, todas as músicas, todos os episódios. */
export interface PlayerContext {
  key: string;
  name: string;
  tracks: PlayerTrack[];
}

/** Velocidades do LeadTalk; o LeadMusic toca sempre a 1x. */
export const PLAYER_SPEEDS = [1, 1.25, 1.5, 2] as const;

interface PlayerState {
  source: PlayerSource | null;
  ctx: PlayerContext | null;
  /** Índices de `ctx.tracks` na ordem em que vão tocar. */
  order: number[];
  /** Índice do item atual em `ctx.tracks`. */
  cur: number;
  playing: boolean;
  /** A fila acabou (sem repetir): o botão principal toca de novo do começo. */
  ended: boolean;
  shuffle: boolean;
  repeat: PlayerRepeat;
  /** Índice em PLAYER_SPEEDS. */
  speed: number;
  /** Toca `ctx` a partir do item `index` (ou do começo); `shuffle` ausente mantém o atual. */
  start: (source: PlayerSource, ctx: PlayerContext, index?: number | null, shuffle?: boolean) => void;
  /** Clique num cartão: o item que já está tocando pausa ou continua; outro começa. */
  playIn: (source: PlayerSource, ctx: PlayerContext, index: number) => void;
  toggle: () => void;
  setPlaying: (playing: boolean) => void;
  /** `auto`: o item acabou sozinho (no fim da fila, para; à mão, não faz nada). */
  next: (auto?: boolean) => void;
  prev: () => void;
  jump: (index: number) => void;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  cycleSpeed: () => void;
  close: () => void;
}

function shuffled(list: number[], first: number | null): number[] {
  const rest = list.filter((i) => i !== first);
  for (let k = rest.length - 1; k > 0; k--) {
    const j = Math.floor(Math.random() * (k + 1));
    const swap = rest[k] as number;
    rest[k] = rest[j] as number;
    rest[j] = swap;
  }
  return first !== null && list.includes(first) ? [first, ...rest] : rest;
}

function naturalOrder(ctx: PlayerContext): number[] {
  return ctx.tracks.map((_, i) => i);
}

/** Item `step` posições depois (ou antes) do atual na fila; volta ao começo com "repetir tudo". */
function stepFrom(s: Pick<PlayerState, 'order' | 'cur' | 'repeat'>, step: number): number | null {
  const k = s.order.indexOf(s.cur);
  if (k < 0) return null;
  const t = k + step;
  if (t >= 0 && t < s.order.length) return s.order[t] ?? null;
  if (s.repeat === 'all' && s.order.length > 1) return s.order[(t + s.order.length) % s.order.length] ?? null;
  return null;
}

export const usePlayerStore = create<PlayerState>((set, get) => ({
  source: null,
  ctx: null,
  order: [],
  cur: 0,
  playing: false,
  ended: false,
  shuffle: false,
  repeat: 'off',
  speed: 0,

  start: (source, ctx, index, shuffle) => {
    if (ctx.tracks.length === 0) return;
    // O LeadTalk toca sempre na ordem dos episódios e sem repetir (decisão do Marcelo, 22/10/2026):
    // o aleatório e o repetir ligados no LeadMusic não passam para lá, que nem tem esses botões.
    const talk = source === 'leadtalk';
    const sh = talk ? false : (shuffle ?? get().shuffle);
    const first = index ?? null;
    const order = sh ? shuffled(naturalOrder(ctx), first) : naturalOrder(ctx);
    set({
      source,
      ctx,
      order,
      cur: first ?? order[0] ?? 0,
      playing: true,
      ended: false,
      shuffle: sh,
      ...(talk ? { repeat: 'off' as const } : {}),
    });
  },

  playIn: (source, ctx, index) => {
    const s = get();
    const current = s.ctx?.tracks[s.cur];
    if (current && current.id === ctx.tracks[index]?.id) {
      if (s.ended) set({ playing: true, ended: false });
      else set({ playing: !s.playing });
      return;
    }
    get().start(source, ctx, index);
  },

  toggle: () => {
    const s = get();
    if (!s.ctx) return;
    if (s.ended && s.source) {
      get().start(s.source, s.ctx, null);
      return;
    }
    set({ playing: !s.playing });
  },

  setPlaying: (playing) => {
    if (get().playing !== playing) set({ playing });
  },

  next: (auto = false) => {
    const s = get();
    const n = stepFrom(s, 1);
    if (n === null) {
      if (auto) set({ playing: false, ended: true });
      return;
    }
    set({ cur: n, ended: false, playing: auto ? true : s.playing });
  },

  prev: () => {
    const p = stepFrom(get(), -1);
    if (p !== null) set({ cur: p, ended: false });
  },

  jump: (index) => set({ cur: index, playing: true, ended: false }),

  toggleShuffle: () => {
    const s = get();
    if (!s.ctx) return;
    const on = !s.shuffle;
    set({ shuffle: on, order: on ? shuffled(naturalOrder(s.ctx), s.cur) : naturalOrder(s.ctx) });
  },

  cycleRepeat: () => {
    const nextRepeat: Record<PlayerRepeat, PlayerRepeat> = { off: 'all', all: 'one', one: 'off' };
    set({ repeat: nextRepeat[get().repeat] });
  },

  cycleSpeed: () => set({ speed: (get().speed + 1) % PLAYER_SPEEDS.length }),

  close: () => set({ source: null, ctx: null, order: [], cur: 0, playing: false, ended: false }),
}));

type QueueState = Pick<PlayerState, 'ctx' | 'order' | 'cur' | 'repeat' | 'ended'>;

/** Item tocando agora (ou pausado). */
export function currentTrack(s: Pick<PlayerState, 'ctx' | 'cur'>): PlayerTrack | null {
  return s.ctx?.tracks[s.cur] ?? null;
}

/** Índices (em `ctx.tracks`) dos próximos `n` itens da fila, já contando o "repetir tudo". */
export function upNext(s: QueueState, n: number): number[] {
  const out: number[] = [];
  const k = s.order.indexOf(s.cur);
  if (k < 0 || s.ended) return out;
  for (let j = 1; j <= n && j < s.order.length; j++) {
    let t = k + j;
    if (t >= s.order.length) {
      if (s.repeat !== 'all') break;
      t %= s.order.length;
    }
    const index = s.order[t];
    if (index !== undefined) out.push(index);
  }
  return out;
}

/** Id do próximo item (para o selo "A seguir" da vitrine). */
export function nextTrackId(s: QueueState): string | null {
  const [n] = upNext(s, 1);
  return n === undefined ? null : (s.ctx?.tracks[n]?.id ?? null);
}

/** Há item antes do atual (senão, "anterior" volta ao começo do item). */
export function hasPrev(s: Pick<PlayerState, 'order' | 'cur' | 'repeat'>): boolean {
  return stepFrom(s, -1) !== null;
}
